import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Input, Select, TextArea, Modal, Table } from '../components/Common';

const ENGAGEMENT_TYPES = [
  'Application Penetration Test',
  'Network Penetration Test',
  'Network Segmentation',
  'Mobile Application Test',
  'Red Team',
  'API Penetration Test'
];

export default function PlaybookTemplates() {
  const { apiFetch, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  const isMsp = user?.isMsp || user?.groups?.includes('Super Admins');

  // Check if the current user can edit/delete a template
  const canManage = (template) => {
    if (isMsp) return true;
    // Clients can only manage templates they created (client_id matches)
    return template.client_id && template.client_id === user?.clientId;
  };

  const loadTemplates = useCallback(async () => {
    try {
      const url = filterType ? `/playbook/templates?type=${encodeURIComponent(filterType)}` : '/playbook/templates';
      const res = await apiFetch(url);
      if (res.ok) setTemplates(await res.json());
    } catch {}
  }, [apiFetch, filterType]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const openAdd = () => {
    setEditing(null);
    setForm({ engagement_type: filterType || ENGAGEMENT_TYPES[0], category: '', title: '', description: '', reference_url: '', sort_order: 0 });
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      engagement_type: t.engagement_type,
      category: t.category,
      title: t.title,
      description: t.description || '',
      reference_url: t.reference_url || '',
      sort_order: t.sort_order || 0,
      is_active: t.is_active
    });
    setShowModal(true);
  };

  const save = async () => {
    setLoading(true);
    try {
      if (editing) {
        await apiFetch(`/playbook/templates/${editing.id}`, { method: 'PUT', body: JSON.stringify(form) });
      } else {
        await apiFetch('/playbook/templates', { method: 'POST', body: JSON.stringify(form) });
      }
      setShowModal(false);
      loadTemplates();
    } catch { alert('Failed to save template'); }
    setLoading(false);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this playbook template? This will not affect already-initialized engagement playbooks.')) return;
    await apiFetch(`/playbook/templates/${id}`, { method: 'DELETE' });
    loadTemplates();
  };

  // Compute stats
  const typeCounts = {};
  templates.forEach(t => { typeCounts[t.engagement_type] = (typeCounts[t.engagement_type] || 0) + 1; });

  // Get unique categories from current templates
  const categories = [...new Set(templates.map(t => t.category))].sort();

  const columns = [
    { key: 'engagement_type', label: 'Type', render: v => <Badge>{v.replace(' Penetration Test', ' PT').replace('Application', 'App')}</Badge> },
    { key: 'category', label: 'Category', render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
    { key: 'title', label: 'Title' },
    { key: 'reference_url', label: 'Reference', render: v => v ? (
      <a href={v} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
        style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>
        {v.includes('cheatsheetseries') ? '📖 Cheat Sheet' :
         v.includes('attack.mitre.org') ? '🎯 ATT&CK' :
         v.includes('mas.owasp.org') ? '📱 MASVS' :
         v.includes('API-Security') ? '🔌 API Top 10' : '📋 OWASP'}
      </a>
    ) : <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>—</span> },
    { key: 'is_active', label: 'Active', render: v => v ? '✅' : '❌' },
    { key: 'client_id', label: 'Scope', render: (v, row) => v
      ? <Badge style={{ background: '#22c55e', color: '#fff' }}>Custom</Badge>
      : <Badge style={{ background: '#6366f1', color: '#fff' }}>Global</Badge>
    },
    { key: 'id', label: 'Actions', render: (v, row) => canManage(row) ? (
      <div style={{ display: 'flex', gap: 6 }}>
        <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={(e) => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
        <Button variant="danger" style={{ padding: '4px 12px', fontSize: 12 }} onClick={(e) => { e.stopPropagation(); deleteTemplate(v); }}>Delete</Button>
      </div>
    ) : <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>—</span> }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Playbook Templates</h1>
        <Button onClick={openAdd}>+ Add Template</Button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        {ENGAGEMENT_TYPES.map(type => (
          <Card key={type} style={{
            padding: 16, textAlign: 'center', cursor: 'pointer',
            border: filterType === type ? '2px solid var(--primary)' : '1px solid var(--border)'
          }} onClick={() => setFilterType(filterType === type ? '' : type)}>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{typeCounts[type] || 0}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {type.replace(' Penetration Test', ' PT').replace('Application', 'App')}
            </div>
          </Card>
        ))}
      </div>

      {/* Filter */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Select label="" style={{ marginBottom: 0, minWidth: 250 }}
            options={[{ value: '', label: 'All Engagement Types' }, ...ENGAGEMENT_TYPES.map(t => ({ value: t, label: t }))]}
            value={filterType} onChange={e => setFilterType(e.target.value)} />
          <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
            {templates.length} template{templates.length !== 1 ? 's' : ''} {filterType ? `for ${filterType}` : 'total'}
          </span>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <Table columns={columns} data={templates} />
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Template' : 'Add Template'} width={600}>
        <Select label="Engagement Type *"
          options={ENGAGEMENT_TYPES.map(t => ({ value: t, label: t }))}
          value={form.engagement_type || ''} onChange={e => setForm(f => ({ ...f, engagement_type: e.target.value }))} />

        <div style={{ position: 'relative' }}>
          <Input label="Category *" placeholder="e.g., Authentication, Input Validation"
            value={form.category || ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
            list="category-suggestions" />
          <datalist id="category-suggestions">
            {categories.map(c => <option key={c} value={c} />)}
          </datalist>
        </div>

        <Input label="Title *" placeholder="Test case name"
          value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />

        <TextArea label="Description" value={form.description || ''}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />

        <Input label="Reference URL" placeholder="https://owasp.org/..." type="url"
          value={form.reference_url || ''} onChange={e => setForm(f => ({ ...f, reference_url: e.target.value }))} />

        <Input label="Sort Order" type="number" min="0"
          value={form.sort_order ?? 0} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />

        {editing && (
          <Select label="Active"
            options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
            value={String(form.is_active !== false)} onChange={e => setForm(f => ({ ...f, is_active: e.target.value === 'true' }))} />
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={save} disabled={!form.engagement_type || !form.category || !form.title || loading}>
            {loading ? 'Saving...' : (editing ? 'Update' : 'Create')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
